/** Inspection draft helpers for ClampQcPage. */
import {
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import {
  type ClampQcInspectionRow,
} from "./clampQcMockData";
import { clampQcMechanicalFailureSkipsOtherTests } from "@integrator-src/config/clampqc-fail-disposition";
import {
  clampQcDragBandForDrag,
  clampQcDragBandForSku,
  clampQcDragFromUnloadedLoadedLines,
  clampQcDragLadderFromSettings,
  clampQcInductanceAcceptanceRangeForSku,
  clampQcInductanceEntryBlocksInspectionSubmit,
  clampQcInductanceReadingIsOutOfBand,
  clampQcLineHarvestFailsFromDragDraft,
  deriveSgsLineHarvestPassForPersist,
  clampQcUnloadedLineAcceptanceRangeForSku,
  clampQcUnloadedLineFailsAcceptance,
  type ClampQcHubSettingsPayload,
} from "./clampQcSettingsApi";
export type PrimaryTab = "inspection" | "timesheet" | "summary" | "reference";
export type ClampSizeTab = "large" | "small";

export interface DraftInspection {
  serial: string;
  unloadedLine: string;
  loadedLineBeforeTester: string;
  /** Gray-band loaded line with tester attached. Empty when drag is not in the gray band. */
  loadedLineWithTester: string;
  /** `null` until the operator picks Good or Failed when the hub requires it (gray drag or unknown SKU). */
  lineHarvestFail: boolean | null;
  inductance: string;
  /** Selected physical fail reason labels (subset of merged hub list). */
  failReasons: string[];
}

export const PRIMARY_TABS: { id: PrimaryTab; label: string }[] = [
  {
    id: "inspection",
    label: "Inspection entry",
  },
  {
    id: "timesheet",
    label: "Timesheet",
  },
  {
    id: "summary",
    label: "Summary",
  },
  {
    id: "reference",
    label: "Reference",
  },
];

export function emptyDraft(): DraftInspection {
  return {
    serial: "",
    unloadedLine: "",
    loadedLineBeforeTester: "",
    loadedLineWithTester: "",
    lineHarvestFail: null,
    inductance: "",
    failReasons: [],
  };
}

export function parseOptionalFloat(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }
  const n = Number.parseFloat(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** Digits and at most one decimal point; strips everything else (paste-safe). */
export function sanitizeDecimalMeasurementInput(raw: string): string {
  const digitsAndDots = raw.replace(/[^0-9.]/g, "");
  const firstDot = digitsAndDots.indexOf(".");
  if (firstDot === -1) {
    return digitsAndDots;
  }
  return (
    digitsAndDots.slice(0, firstDot + 1) + digitsAndDots.slice(firstDot + 1).replace(/\./g, "")
  );
}

export function inductanceMeasurementKeyDown(event: ReactKeyboardEvent<HTMLInputElement>): void {
  if (event.nativeEvent.isComposing) {
    return;
  }
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return;
  }
  const navigationKeys = new Set([
    "Backspace",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
    "Delete",
  ]);
  if (navigationKeys.has(event.key)) {
    return;
  }
  if (event.key >= "0" && event.key <= "9") {
    return;
  }
  if (event.key === ".") {
    return;
  }
  event.preventDefault();
}

export function focusElementNextTick(element: HTMLElement | null | undefined): void {
  if (element == null) {
    return;
  }
  window.setTimeout(() => {
    element.focus();
  }, 0);
}

/**
 * Decimal measurement inputs: filter keys, and Enter commits (blur) then moves focus for tenkey flow.
 */
export function measurementInputKeyDownWithEnterFocusNext(
  event: ReactKeyboardEvent<HTMLInputElement>,
  next: HTMLElement | null | undefined,
): void {
  if (event.nativeEvent.isComposing) {
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    event.currentTarget.blur();
    focusElementNextTick(next ?? null);
    return;
  }
  inductanceMeasurementKeyDown(event);
}

/**
 * Single-key shortcuts: slots 1–9 via keys 1–9, slot 10 via 0 (when at least ten reasons). Numpad digit keys match.
 * Slots are 1-based; list positions are shown before each label.
 */
export function failReasonKeypadSingleKeySlot(key: string): number | null {
  if (/^[1-9]$/.test(key)) {
    return Number.parseInt(key, 10);
  }
  if (key === "0") {
    return 10;
  }
  const numpad = /^Numpad(\d)$/.exec(key);
  if (numpad !== null) {
    const digit = Number.parseInt(numpad[1]!, 10);
    return digit === 0 ? 10 : digit;
  }
  return null;
}

export function failReasonKeypadStarArmKey(key: string): boolean {
  return key === "*" || key === "NumpadMultiply";
}

export function failReasonKeypadDigitCharacter(key: string): string | null {
  if (/^[0-9]$/.test(key)) {
    return key;
  }
  const numpad = /^Numpad(\d)$/.exec(key);
  if (numpad !== null) {
    return numpad[1]!;
  }
  return null;
}

export function failReasonsKeypadHelpText(): string {
  return (
    "Select any reasons that apply; the unit fails if one or more are checked. " +
    "Numbers at the left of each row are list positions (same order as the checkboxes). " +
    "Keys 1–9 on the main or numeric keypad toggle positions 1–9. Key 0 toggles position 10 when there are at least ten reasons. " +
    "When there are eleven or more reasons, press * (Shift+8 on the main keyboard or multiply on the numeric keypad), type the position using digits with no leading zeros (for example 11 for the eleventh reason), then press Enter to toggle that row. " +
    "Press * again, or Enter before typing any digit, to cancel. Backspace removes the last digit while typing after *. " +
    "These shortcuts are ignored while focus is in inductance, unloaded line, loaded line, loaded line with tester, or the New reason for everyone field."
  );
}

export function physicalInspectionModalDigitTargetIgnoresFailKeypad(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const formControl = target.closest("input, textarea, select");
  if (formControl instanceof HTMLTextAreaElement || formControl instanceof HTMLSelectElement) {
    return true;
  }
  if (!(formControl instanceof HTMLInputElement)) {
    return false;
  }
  const id = formControl.id;
  if (
    id === "qc-modal-inductance" ||
    id === "qc-modal-unloaded" ||
    id === "qc-modal-loaded" ||
    id === "qc-modal-loaded-with-tester" ||
    id === "qc-modal-fail-new"
  ) {
    return true;
  }
  if (formControl.type !== "checkbox" && formControl.type !== "radio") {
    return true;
  }
  return false;
}

export type ClampQcInductanceEvaluationOptions = {
  /** True when workflow.clamp_qc_inspection_slot reserved this serial for stratified inductance sampling. */
  inductanceSampled: boolean;
};

export function inductanceSampledFromOptions(options?: ClampQcInductanceEvaluationOptions): boolean {
  return options?.inductanceSampled === true;
}

export function inductanceSectionHelpText(jobSku: string, hubSettings: ClampQcHubSettingsPayload | null): string {
  if (hubSettings == null) {
    return "Hub settings are not loaded. Inductance acceptance text applies after saved settings can be read from the server.";
  }
  const percent = String(hubSettings.inductanceTestingPercentPerWorkOrder);
  const range = clampQcInductanceAcceptanceRangeForSku(jobSku, hubSettings);
  const bandText =
    range == null
      ? "Hub settings do not define an automatic inductance band for this job SKU."
      : `Inclusive acceptance band for this job: ${String(range.min)} — ${String(range.max)}.`;
  return `${percent}% of planned units get an inductance reading. ${bandText} The field is disabled when this unit is not in the sample; there is no voluntary inductance entry on non-sampled units.`;
}

export function lineHarvestSectionHelpText(jobSku: string, hubSettings: ClampQcHubSettingsPayload | null): string {
  if (hubSettings == null) {
    return "Clamp QC hub settings are not loaded. Save settings on the Clamp QC Settings page or fix the API error before using line harvest rules.";
  }
  const ladder = clampQcDragLadderFromSettings(jobSku, hubSettings);
  if (ladder == null) {
    return (
      "This job SKU does not start with 90-002 or 90-003, so hub drag windows are not applied. Line harvest Good or Failed is manual. " +
      "Unloaded line is remembered for the next clamp on this job."
    );
  }
  const unloadedRange = clampQcUnloadedLineAcceptanceRangeForSku(jobSku, hubSettings);
  const unloadedText =
    unloadedRange == null
      ? ""
      : ` Unloaded line must be ${String(unloadedRange.min)}-${String(unloadedRange.max)} (inclusive) before recording. Outside that band, Record stays disabled and the unit is not failed or scrapped.`;
  return (
    `Pass or fail is the drag delta: unloaded line minus loaded line before tester. Prefix 90-003 and prefix 90-002 use separate unloaded bands and separate drag windows. ` +
    `Gray window ${String(ladder.grayMin)}-${String(ladder.grayMax)}: marginal, connect the unit to a line tester, enter loaded line with tester, then choose Line harvest Good or Failed. ` +
    `Pass band ${String(ladder.passMin)}-${String(ladder.passMax)}: line harvest passes automatically. Outside the gray window: fails automatically. The loaded line reading is not a pass or fail by itself.${unloadedText} Unloaded line persists for the next clamp on this job.`
  );
}

/**
 * Maps the operator-entered serial to the numeric key stored on inspection rows. In development, non-integer barcodes
 * hash to a stable positive integer so scanners can send alphanumeric tokens without changing the row type.
 * Domestic clamp label `US` always hashes the same way in production.
 */
export function inspectionSerialToStableKey(trimmedSerial: string, allowNonNumeric: boolean): number | null {
  if (trimmedSerial === "") {
    return null;
  }
  const asInt = Number.parseInt(trimmedSerial, 10);
  if (Number.isFinite(asInt) && String(asInt) === trimmedSerial) {
    return asInt < 0 ? null : asInt;
  }
  if (!allowNonNumeric && !/^[Uu][Ss]$/.test(trimmedSerial)) {
    return null;
  }
  let hash = 2166136261 >>> 0;
  for (let index = 0; index < trimmedSerial.length; index += 1) {
    hash ^= trimmedSerial.charCodeAt(index);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return hash === 0 ? 1 : hash;
}

function draftUnloadedOutOfRange(
  draft: DraftInspection,
  jobSku: string,
  hubSettings: ClampQcHubSettingsPayload,
): boolean {
  const unloaded = parseOptionalFloat(draft.unloadedLine);
  if (unloaded === null) {
    return false;
  }
  return clampQcUnloadedLineFailsAcceptance(unloaded, jobSku, hubSettings);
}

/**
 * Stored row result is derived from inspection answers (no separate Passed/Failed control).
 * Inductance out of band uses hub settings for the job SKU line (`90-003*` vs `90-002*`).
 * Unloaded line outside its band blocks submit and is not itself a fail.
 */
export function computedInspectionResult(
  draft: DraftInspection,
  jobSku: string,
  hubSettings: ClampQcHubSettingsPayload | null,
  options?: ClampQcInductanceEvaluationOptions,
): "Passed" | "Failed" {
  if (hubSettings == null) {
    return "Failed";
  }
  const mechanicalFailSkipsOtherTests = clampQcMechanicalFailureSkipsOtherTests(draft.failReasons);
  const inductanceSampled = inductanceSampledFromOptions(options) && !mechanicalFailSkipsOtherTests;
  const drag = clampQcDragFromUnloadedLoadedLines(draft.unloadedLine, draft.loadedLineBeforeTester);
  if (
    !mechanicalFailSkipsOtherTests &&
    clampQcLineHarvestFailsFromDragDraft(draft.lineHarvestFail, drag, jobSku, hubSettings)
  ) {
    return "Failed";
  }
  if (draft.failReasons.length > 0) {
    return "Failed";
  }
  if (
    inductanceSampled &&
    clampQcInductanceReadingIsOutOfBand(draft.inductance, jobSku, hubSettings)
  ) {
    return "Failed";
  }
  return "Passed";
}

/** True when the operator has entered a real failure signal (not merely missing measurements). */
export function inspectionModalHasExplicitFailCriteria(
  committed: DraftInspection,
  jobSku: string,
  hubSettings: ClampQcHubSettingsPayload | null,
  options?: ClampQcInductanceEvaluationOptions,
): boolean {
  if (hubSettings == null) {
    return false;
  }
  const inductanceSampled = inductanceSampledFromOptions(options);
  if (committed.failReasons.length > 0) {
    return true;
  }
  if (
    inductanceSampled &&
    clampQcInductanceReadingIsOutOfBand(committed.inductance, jobSku, hubSettings)
  ) {
    return true;
  }
  if (committed.lineHarvestFail === true) {
    return true;
  }
  const drag = clampQcDragFromUnloadedLoadedLines(committed.unloadedLine, committed.loadedLineBeforeTester);
  const ladder = clampQcDragLadderFromSettings(jobSku, hubSettings);
  if (ladder !== null && drag !== null) {
    const band = clampQcDragBandForDrag(drag, ladder);
    if (band === "fail") {
      return true;
    }
  }
  return false;
}

/**
 * True while required modal answers are still missing and there is no explicit fail yet.
 * Missing drag or unset line harvest (when required) does not count as a fail for the Record button.
 */
export function inspectionModalFormIncomplete(
  committed: DraftInspection,
  jobSku: string,
  hubSettings: ClampQcHubSettingsPayload | null,
  options?: ClampQcInductanceEvaluationOptions,
): boolean {
  if (hubSettings == null) {
    return true;
  }
  const mechanicalFailSkipsOtherTests = clampQcMechanicalFailureSkipsOtherTests(committed.failReasons);
  const inductanceSampled = inductanceSampledFromOptions(options) && !mechanicalFailSkipsOtherTests;
  const inductanceOutOfBand =
    inductanceSampled && clampQcInductanceReadingIsOutOfBand(committed.inductance, jobSku, hubSettings);
  const lineHarvestRequired =
    !mechanicalFailSkipsOtherTests && !(inductanceSampled && inductanceOutOfBand);
  if (lineHarvestRequired && draftUnloadedOutOfRange(committed, jobSku, hubSettings)) {
    return true;
  }
  const ladder = clampQcDragLadderFromSettings(jobSku, hubSettings);
  const drag = clampQcDragFromUnloadedLoadedLines(committed.unloadedLine, committed.loadedLineBeforeTester);
  const band = clampQcDragBandForSku(drag, jobSku, hubSettings);
  if (lineHarvestRequired && band === "gray" && parseOptionalFloat(committed.loadedLineWithTester) === null) {
    return true;
  }
  if (inspectionModalHasExplicitFailCriteria(committed, jobSku, hubSettings, options)) {
    return false;
  }
  if (lineHarvestRequired && ladder !== null && drag === null) {
    return true;
  }
  const needsHarvestChoice = ladder === null || band === "gray";
  if (lineHarvestRequired && needsHarvestChoice && committed.lineHarvestFail === null) {
    return true;
  }
  if (clampQcInductanceEntryBlocksInspectionSubmit(committed.inductance, inductanceSampled)) {
    return true;
  }
  return false;
}

export function draftToRow(
  draft: DraftInspection,
  submittedAtIso: string,
  serialKey: number,
  jobSku: string,
  hubSettings: ClampQcHubSettingsPayload,
  options?: ClampQcInductanceEvaluationOptions,
): ClampQcInspectionRow {
  const mechanicalFailSkipsOtherTests = clampQcMechanicalFailureSkipsOtherTests(draft.failReasons);
  const inductanceSampled = inductanceSampledFromOptions(options) && !mechanicalFailSkipsOtherTests;
  const inductanceOutOfRange =
    inductanceSampled && clampQcInductanceReadingIsOutOfBand(draft.inductance, jobSku, hubSettings);
  const lineHarvestSkipped =
    mechanicalFailSkipsOtherTests || (inductanceSampled && inductanceOutOfRange);
  const drag = lineHarvestSkipped
    ? null
    : clampQcDragFromUnloadedLoadedLines(draft.unloadedLine, draft.loadedLineBeforeTester);
  const lineHarvestPass = lineHarvestSkipped
    ? null
    : deriveSgsLineHarvestPassForPersist({
        lineHarvestFailManual: draft.lineHarvestFail,
        drag,
        sku: jobSku,
        settings: hubSettings,
      });
  const dragBandForRow = lineHarvestSkipped ? "unknown" : clampQcDragBandForSku(drag, jobSku, hubSettings);
  const testerHookedRecorded = dragBandForRow === "gray";
  return {
    crate: null,
    timestampIso: submittedAtIso,
    boxSerial: 0,
    serial: serialKey,
    unloadedLine: lineHarvestSkipped ? null : parseOptionalFloat(draft.unloadedLine),
    loadedLineBeforeTester: lineHarvestSkipped ? null : parseOptionalFloat(draft.loadedLineBeforeTester),
    loadedLineWithTester: lineHarvestSkipped ? null : parseOptionalFloat(draft.loadedLineWithTester),
    testerA: testerHookedRecorded,
    testerB: false,
    testerC: false,
    lineHarvestFail: lineHarvestPass === null ? null : !lineHarvestPass,
    inductance: inductanceSampled ? parseOptionalFloat(draft.inductance) : null,
    inductancePassFail: inductanceOutOfRange ? "Out of range" : null,
    failReason:
      draft.failReasons.length === 0
        ? null
        : [...draft.failReasons]
            .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }))
            .join("; "),
    observations: null,
    result: computedInspectionResult(draft, jobSku, hubSettings, options),
    delta: drag,
  };
}

export function hubOperatorAttribution(
  me: { user: { displayName?: string | null } } | undefined,
  email: string,
): string {
  const name = me?.user.displayName?.trim();
  if (name) {
    return name;
  }
  const local = email.split("@")[0]?.trim();
  return local && local !== "" ? local : "Signed-in user";
}
